import "../css/header.scss";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";

const getShopPath = (item) => {
  if (item.category) {
    return `/shop?category=${encodeURIComponent(item.category)}`;
  }

  return `/shop?tag=${encodeURIComponent(item.tag)}`;
};

const getSearchText = (product) =>
  [
    product.name,
    product.category,
    product.productType,
    product.description,
    ...(Array.isArray(product.tags) ? product.tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

function Header() {
  const {
    cartItems = [],
    wishlistItems = [],
    removeFromWishlist,
    toggleWishlist,
    hasInWishlist,
  } = useContext(CartContext);
  const [promotionIndex, setPromotionIndex] = useState(0);
  const [activePanel, setActivePanel] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountForm, setAccountForm] = useState({ email: "", password: "" });
  const [accountStatus, setAccountStatus] = useState("");
  const searchInputRef = useRef(null);
  const activePanelRef = useRef(null);
  const lastTriggerRef = useRef(null);
  const promotions = siteConfig.promotions || [];

  const cartCount = cartItems.reduce((total, item) => {
    const isKnownProduct = productData.some(
      (product) => String(product.id) === String(item.id)
    );
    if (!isKnownProduct) return total;
    const quantity = Number(item.quantity);
    return total + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);

  const wishlistProducts = useMemo(
    () =>
      wishlistItems
        .map((wishlistItem) =>
          productData.find(
            (product) =>
              String(product.id) ===
              String(
                typeof wishlistItem === "object"
                  ? wishlistItem.id
                  : wishlistItem
              )
          )
        )
        .filter(Boolean),
    [wishlistItems]
  );

  const searchResults = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      const featuredProducts = productData.filter(
        (product) => product.featured
      );
      return (featuredProducts.length ? featuredProducts : productData).slice(
        0,
        5
      );
    }

    return productData
      .filter((product) => getSearchText(product).includes(normalizedTerm))
      .slice(0, 6);
  }, [searchTerm]);

  useEffect(() => {
    if (promotions.length < 2) return undefined;

    const intervalId = window.setInterval(() => {
      setPromotionIndex(
        (currentIndex) => (currentIndex + 1) % promotions.length
      );
    }, 5500);

    return () => window.clearInterval(intervalId);
  }, [promotions.length]);

  useEffect(() => {
    if (!activePanel) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePanel(null);
        window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
        return;
      }

      if (event.key !== "Tab" || !activePanelRef.current) return;

      const focusableElements = Array.from(
        activePanelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

      if (!focusableElements.length) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === firstElement ||
          !activePanelRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastElement ||
          !activePanelRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activePanel]);

  useEffect(() => {
    if (!activePanel) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activePanel]);

  useEffect(() => {
    if (activePanel !== "search") return undefined;

    const focusTimer = window.setTimeout(
      () => searchInputRef.current?.focus(),
      0
    );
    return () => window.clearTimeout(focusTimer);
  }, [activePanel]);

  useEffect(() => {
    if (!activePanel || activePanel === "search") return undefined;

    const focusTimer = window.setTimeout(() => {
      activePanelRef.current?.querySelector("button, a[href], input")?.focus();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [activePanel]);

  const closePanels = (restoreFocus = true) => {
    setActivePanel(null);
    if (restoreFocus && lastTriggerRef.current) {
      window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
    }
  };

  const openPanel = (panelName, event) => {
    lastTriggerRef.current = event.currentTarget;
    setActivePanel((currentPanel) =>
      currentPanel === panelName ? null : panelName
    );
  };

  const handleLinkClick = () => {
    closePanels(false);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const showPreviousPromotion = () => {
    setPromotionIndex(
      (currentIndex) =>
        (currentIndex - 1 + promotions.length) % promotions.length
    );
  };

  const showNextPromotion = () => {
    setPromotionIndex((currentIndex) => (currentIndex + 1) % promotions.length);
  };

  return (
    <header className="site-header">
      {promotions.length > 0 && (
        <div className="site-header__promotion" aria-label="Current offers">
          <button
            type="button"
            className="site-header__promotion-control"
            onClick={showPreviousPromotion}
            aria-label="Previous offer"
          >
            <FiChevronLeft aria-hidden="true" />
          </button>
          <p aria-live="polite">{promotions[promotionIndex]}</p>
          <button
            type="button"
            className="site-header__promotion-control"
            onClick={showNextPromotion}
            aria-label="Next offer"
          >
            <FiChevronRight aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="site-header__surface">
        <div className="site-header__desktop-row">
          <button
            type="button"
            className="site-header__search-trigger"
            onClick={(event) => openPanel("search", event)}
            aria-expanded={activePanel === "search"}
            aria-controls="header-search-panel"
          >
            <FiSearch aria-hidden="true" />
            <span>Search</span>
          </button>

          <Link
            className="site-header__wordmark"
            to="/"
            onClick={handleLinkClick}
            aria-label={`${siteConfig.brandName} home`}
          >
            <img src="/head/pirecobalt-logo.svg" alt={siteConfig.brandName} />
          </Link>

          <div className="site-header__actions">
            <button
              type="button"
              className="site-header__icon-button"
              onClick={(event) => openPanel("wishlist", event)}
              aria-label={`Open wishlist, ${wishlistProducts.length} items`}
              aria-expanded={activePanel === "wishlist"}
              aria-controls="header-wishlist-panel"
            >
              <FiHeart aria-hidden="true" />
              <span className="site-header__count" aria-hidden="true">
                {wishlistProducts.length}
              </span>
            </button>
            <Link
              className="site-header__icon-button"
              to="/cartpage"
              onClick={handleLinkClick}
              aria-label={`Shopping bag, ${cartCount} items`}
            >
              <FiShoppingBag aria-hidden="true" />
              <span className="site-header__count" aria-hidden="true">
                {cartCount}
              </span>
            </Link>
          </div>
        </div>

        <div className="site-header__mobile-row">
          <div className="site-header__mobile-group">
            <button
              type="button"
              className="site-header__icon-button"
              onClick={(event) => openPanel("menu", event)}
              aria-label="Open menu"
              aria-expanded={activePanel === "menu"}
              aria-controls="header-mobile-menu"
            >
              <FiMenu aria-hidden="true" />
            </button>
            <button
              type="button"
              className="site-header__icon-button"
              onClick={(event) => openPanel("search", event)}
              aria-label="Open search"
              aria-expanded={activePanel === "search"}
              aria-controls="header-search-panel"
            >
              <FiSearch aria-hidden="true" />
            </button>
          </div>

          <Link
            className="site-header__wordmark"
            to="/"
            onClick={handleLinkClick}
            aria-label={`${siteConfig.brandName} home`}
          >
            <img src="/head/pirecobalt-logo.svg" alt={siteConfig.brandName} />
          </Link>

          <div className="site-header__mobile-group site-header__mobile-group--right">
            <button
              type="button"
              className="site-header__icon-button"
              onClick={(event) => openPanel("wishlist", event)}
              aria-label={`Open wishlist, ${wishlistProducts.length} items`}
              aria-expanded={activePanel === "wishlist"}
              aria-controls="header-wishlist-panel"
            >
              <FiHeart aria-hidden="true" />
              <span className="site-header__count" aria-hidden="true">
                {wishlistProducts.length}
              </span>
            </button>
            <Link
              className="site-header__icon-button"
              to="/cartpage"
              onClick={handleLinkClick}
              aria-label={`Shopping bag, ${cartCount} items`}
            >
              <FiShoppingBag aria-hidden="true" />
              <span className="site-header__count" aria-hidden="true">
                {cartCount}
              </span>
            </Link>
          </div>
        </div>

        <nav
          className="site-header__navigation"
          aria-label="Primary navigation"
        >
          <Link to="/shop" onClick={handleLinkClick}>
            Shop all
          </Link>
          {siteConfig.navigation.map((item) => (
            <Link
              key={item.label}
              to={getShopPath(item)}
              onClick={handleLinkClick}
            >
              {item.label}
            </Link>
          ))}
          <details className="site-header__more">
            <summary>More</summary>
            <div>
              <Link to="/privacy" onClick={handleLinkClick}>
                Privacy
              </Link>
              <Link to="/cookies" onClick={handleLinkClick}>
                Cookies
              </Link>
              <Link to="/faqs" onClick={handleLinkClick}>
                FAQs
              </Link>
              <Link to="/terms" onClick={handleLinkClick}>
                Terms
              </Link>
            </div>
          </details>
        </nav>
      </div>

      {activePanel && (
        <button
          type="button"
          className="site-header__backdrop"
          onClick={closePanels}
          aria-label="Close open panel"
        />
      )}

      {activePanel === "search" && (
        <section
          ref={activePanelRef}
          className="header-search"
          id="header-search-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="header-search-title"
        >
          <div className="header-search__topline">
            <h2 id="header-search-title">Find something thoughtful</h2>
            <button
              type="button"
              className="header-panel__close"
              onClick={closePanels}
              aria-label="Close search"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>
          <form
            className="header-search__form"
            role="search"
            onSubmit={(event) => event.preventDefault()}
          >
            <FiSearch aria-hidden="true" />
            <label className="header-visually-hidden" htmlFor="site-search">
              Search products
            </label>
            <input
              id="site-search"
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search notebooks, planners, gifts…"
              autoComplete="off"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <FiX aria-hidden="true" />
              </button>
            )}
          </form>

          <div className="header-search__results" aria-live="polite">
            <p className="header-search__eyebrow">
              {searchTerm.trim()
                ? `${searchResults.length} results`
                : "Most loved"}
            </p>
            {searchResults.length > 0 ? (
              <div className="header-search__grid">
                {searchResults.map((product) => {
                  const wishlisted = hasInWishlist(product.id);
                  return (
                    <article className="header-search-card" key={product.id}>
                      <Link
                        className="header-search-card__link"
                        to={`/shopDetails/${product.id}`}
                        onClick={handleLinkClick}
                      >
                        <img src={product.image} alt="" loading="lazy" />
                        <span className="header-search-card__copy">
                          <small>
                            {product.productType || product.category}
                          </small>
                          <strong>{product.name}</strong>
                          <span>{formatPrice(product.price)}</span>
                        </span>
                      </Link>
                      <button
                        type="button"
                        className="header-search-card__wishlist"
                        onClick={() => toggleWishlist(product.id)}
                        aria-label={`${wishlisted ? "Remove" : "Add"} ${
                          product.name
                        } ${wishlisted ? "from" : "to"} wishlist`}
                        aria-pressed={wishlisted}
                      >
                        <FiHeart aria-hidden="true" />
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="header-search__empty">
                <p>No matches yet. Try a category or a shorter phrase.</p>
                <Link to="/shop" onClick={handleLinkClick}>
                  Browse everything <FiArrowRight aria-hidden="true" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {activePanel === "wishlist" && (
        <aside
          ref={activePanelRef}
          className="header-wishlist"
          id="header-wishlist-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="header-wishlist-title"
        >
          <div className="header-panel__heading">
            <div>
              <p>Saved for later</p>
              <h2 id="header-wishlist-title">Your wishlist</h2>
            </div>
            <button
              type="button"
              className="header-panel__close"
              onClick={closePanels}
              aria-label="Close wishlist"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>

          {wishlistProducts.length > 0 ? (
            <div className="header-wishlist__items">
              {wishlistProducts.map((product) => (
                <article className="header-wishlist__item" key={product.id}>
                  <Link
                    to={`/shopDetails/${product.id}`}
                    onClick={handleLinkClick}
                  >
                    <img src={product.image} alt="" loading="lazy" />
                    <span>
                      <small>{product.productType || product.category}</small>
                      <strong>{product.name}</strong>
                      <span>{formatPrice(product.price)}</span>
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => removeFromWishlist(product.id)}
                    aria-label={`Remove ${product.name} from wishlist`}
                  >
                    <FiTrash2 aria-hidden="true" />
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="header-wishlist__empty">
              <FiHeart aria-hidden="true" />
              <h3>Keep your favourites close</h3>
              <p>Tap the heart on a product to save it here.</p>
              <Link to="/shop" onClick={handleLinkClick}>
                Explore the collection
              </Link>
            </div>
          )}
        </aside>
      )}

      {activePanel === "account" && (
        <aside
          ref={activePanelRef}
          className="header-account"
          id="header-account-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="header-account-title"
        >
          <div className="header-panel__heading">
            <div>
              <p>Your paper place</p>
              <h2 id="header-account-title">Log in</h2>
            </div>
            <button
              type="button"
              className="header-panel__close"
              onClick={closePanels}
              aria-label="Close account login"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>
          <form
            className="header-account__form"
            onSubmit={(event) => {
              event.preventDefault();
              setAccountStatus(
                "Demo login complete. Your favourites remain saved on this device."
              );
            }}
          >
            <p>
              Keep favourite designs close and make your next visit a little
              quicker.
            </p>
            <label htmlFor="account-email">Email address</label>
            <input
              id="account-email"
              type="email"
              value={accountForm.email}
              onChange={(event) => {
                setAccountForm((current) => ({
                  ...current,
                  email: event.target.value,
                }));
                setAccountStatus("");
              }}
              autoComplete="email"
              required
            />
            <label htmlFor="account-password">Password</label>
            <input
              id="account-password"
              type="password"
              value={accountForm.password}
              onChange={(event) => {
                setAccountForm((current) => ({
                  ...current,
                  password: event.target.value,
                }));
                setAccountStatus("");
              }}
              autoComplete="current-password"
              minLength="6"
              required
            />
            <button type="submit">Log in securely</button>
            {accountStatus && <p role="status">{accountStatus}</p>}
            <small>
              Demonstration only — details stay in this page and are not sent.
            </small>
          </form>
        </aside>
      )}

      {activePanel === "menu" && (
        <aside
          ref={activePanelRef}
          className="header-menu"
          id="header-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-labelledby="header-menu-title"
        >
          <div className="header-panel__heading">
            <h2 id="header-menu-title" className="header-menu__wordmark">
              <img src="/head/pirecobalt-logo.svg" alt={siteConfig.brandName} />
            </h2>
            <button
              type="button"
              className="header-panel__close"
              onClick={closePanels}
              aria-label="Close menu"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>
          <nav
            className="header-menu__navigation"
            aria-label="Mobile navigation"
          >
            <Link to="/shop" onClick={handleLinkClick}>
              <span>Shop all</span>
              <FiArrowRight aria-hidden="true" />
            </Link>
            {siteConfig.navigation.map((item) => (
              <Link
                key={item.label}
                to={getShopPath(item)}
                onClick={handleLinkClick}
              >
                <span>{item.label}</span>
                <FiArrowRight aria-hidden="true" />
              </Link>
            ))}
          </nav>
          <div className="header-menu__secondary">
            <button type="button" onClick={() => setActivePanel("account")}>
              Log in
            </button>
            <Link to="/about" onClick={handleLinkClick}>
              Our story
            </Link>
            <Link to="/shipping" onClick={handleLinkClick}>
              Delivery
            </Link>
            <Link to="/returns" onClick={handleLinkClick}>
              Returns
            </Link>
            <Link to="/contact" onClick={handleLinkClick}>
              Help &amp; contact
            </Link>
            <Link to="/privacy" onClick={handleLinkClick}>
              Privacy
            </Link>
            <Link to="/cookies" onClick={handleLinkClick}>
              Cookies
            </Link>
            <Link to="/faqs" onClick={handleLinkClick}>
              FAQs
            </Link>
          </div>
          <p className="header-menu__note">{siteConfig.description}</p>
        </aside>
      )}
    </header>
  );
}

export default Header;
